import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';

// Regex to match {{PLACEHOLDER}} patterns
const PLACEHOLDER_REGEX = /\{\{([A-Z_]+)\}\}/g;

/**
 * Node view component for placeholder pills
 */
function PlaceholderPill({ node }: NodeViewProps) {
  const name = node.attrs.name as string;
  // Format name: TEST_OVERVIEW -> Test Overview
  const displayName = name
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

  return (
    <NodeViewWrapper as="span" className="inline">
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
        contentEditable={false}
      >
        {displayName}
      </span>
    </NodeViewWrapper>
  );
}

/**
 * TipTap extension for template placeholders like {{TEST_OVERVIEW}}
 * Renders them as styled pills in the editor
 */
export const TemplatePlaceholderExtension = Node.create({
  name: 'templatePlaceholder',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: element => element.getAttribute('data-name'),
        renderHTML: attributes => ({
          'data-name': attributes.name,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="placeholder"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'placeholder' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderPill);
  },

  addProseMirrorPlugins() {
    const placeholderType = this.type;

    return [
      new Plugin({
        key: new PluginKey('placeholderDecorations'),

        // Input rule to convert {{PLACEHOLDER}} text to placeholder node
        appendTransaction(_transactions, _oldState, newState) {
          const tr = newState.tr;
          let modified = false;

          newState.doc.descendants((node, pos) => {
            if (node.isText && node.text) {
              const matches = [...node.text.matchAll(PLACEHOLDER_REGEX)];

              // Process matches in reverse order to maintain positions
              for (let i = matches.length - 1; i >= 0; i--) {
                const match = matches[i];
                if (match.index !== undefined) {
                  const from = pos + match.index;
                  const to = from + match[0].length;
                  const placeholderNode = placeholderType.create({ name: match[1] });

                  tr.replaceWith(from, to, placeholderNode);
                  modified = true;
                }
              }
            }
          });

          return modified ? tr : null;
        },
      }),
    ];
  },
});

/**
 * Convert placeholder nodes back to {{PLACEHOLDER}} text for markdown output
 */
export function placeholderToMarkdown(node: { attrs: { name: string } }): string {
  return `{{${node.attrs.name}}}`;
}
